/**
 * What the `claude` account's plan usage says about the week: how far through
 * its window now is and how far usage has run ahead of that (the strip's "12%
 * over", and auto-fix's limit), and which USAGE STATE that puts it in
 * (`usageState`) — what a queued row started "Under" or "Wastage" waits for.
 * Here once, so the Claude strip and the server cannot disagree.
 */
import { HOUR_MS, type ClaudeUsage, type OutsideUsage, type UsageWindow } from "./types.js";

export const WEEK_MS = 7 * 24 * 3600_000;

/**
 * Roughly how much of the weekly allowance one five-hour window used to its
 * limit takes. A guess from watching the two meters, not anything the
 * endpoint reports: change it here when the plan does.
 */
export const SESSION_WEEKLY_PERCENT = 5;
export const SESSION_HOURS = 5;
/**
 * The share of each five-hour window "Under" lets itself spend: half, so a
 * burst of queued work never runs one dry. Past it the state is "held".
 */
export const UNDER_SESSION_SHARE = 0.5;
/** A reading older than this is drawn faded, and is not acted on. */
export const USAGE_STALE_MS = 15 * 60_000;

/** How far through the weekly window `now` is (0..100), worked back from its reset. */
export function weekElapsed(resetsAt: number | null | undefined, now: number): number | null {
  if (!resetsAt) return null;
  return Math.min(100, Math.max(0, ((WEEK_MS - (resetsAt - now)) / WEEK_MS) * 100));
}

/** Points of the week's allowance used beyond the share of the week gone,
 *  rounded as the strip shows it: above 0 is spending too fast. Null when the
 *  reading does not say when the week resets. */
export function weeklyAhead(weekly: UsageWindow | null | undefined, now: number): number | null {
  const through = weekElapsed(weekly?.resetsAt, now);
  return weekly && through !== null ? Math.round(weekly.percent - through) : null;
}

/** How far through an open five-hour window `now` is (0..100), worked back from
 *  its reset; null while none is open (no reset ahead). */
export function fiveHourElapsed(resetsAt: number | null | undefined, now: number): number | null {
  if (!resetsAt || resetsAt <= now) return null;
  const span = SESSION_HOURS * HOUR_MS;
  return Math.min(100, Math.max(0, ((span - (resetsAt - now)) / span) * 100));
}

/**
 * Whether the five-hour window has room at "Under"'s pace: no more of it used
 * than `UNDER_SESSION_SHARE` of the share of it gone — 20% of the window's time
 * through, up to 10% of its tokens. A window not open (no reset ahead) or with
 * nothing spent yet always has room: the first start is what opens it, and 0%
 * of 0% would never let one through. No five-hour reading at all says nothing
 * against it.
 */
export function fiveHourHasRoom(usage: ClaudeUsage | null, now: number): boolean {
  const w = usage?.fiveHour;
  const through = fiveHourElapsed(w?.resetsAt, now);
  if (!w || through === null || w.percent <= 0) return true;
  return w.percent < through * UNDER_SESSION_SHARE;
}

/**
 * The fewest hours that would use up what is left of the week flat out: every
 * five-hour window run to its limit, back to back, whole windows. With fewer
 * hours than that left in the week, some of it will go unspent whatever runs.
 */
export function weeklyHoursFlatOut(usage: ClaudeUsage | null): number | null {
  const w = usage?.weekly;
  if (!w) return null;
  return Math.ceil(Math.max(0, 100 - w.percent) / SESSION_WEEKLY_PERCENT) * SESSION_HOURS;
}

/** A reading fit to act on — never a stale or failed one (it would overstate
 *  what is left) — with a weekly window that says when it resets. */
function fresh(usage: ClaudeUsage | null, now: number): usage is ClaudeUsage & { weekly: UsageWindow & { resetsAt: number } } {
  return !!usage && !usage.error && !!usage.readAt && now - usage.readAt <= USAGE_STALE_MS && !!usage.weekly?.resetsAt;
}

/**
 * Where the plan usage stands, checked in this order:
 *
 * - `unknown`: no reading fit to act on (none, stale, failed, or no weekly reset).
 * - `atLimit`: the week or an open five-hour window is used up — nothing can run.
 * - `wastage`: fewer hours left in the week than even flat-out use needs to spend
 *   what is left (`weeklyHoursFlatOut`) — allowance is being lost.
 * - `over`: more of the week's allowance used than the share of the week gone.
 * - `held`: under that, but the five-hour window is past half its pace (`fiveHourHasRoom`).
 * - `under`: under the week's pace, with room in the window.
 *
 * Wastage comes before over: by then usage is always well under pace anyway.
 */
export type UsageState = "unknown" | "atLimit" | "wastage" | "over" | "held" | "under";

export function usageState(usage: ClaudeUsage | null, now: number): UsageState {
  if (!fresh(usage, now)) return "unknown";
  const { weekly, fiveHour } = usage;
  if (weekly.percent >= 100) return "atLimit";
  if (fiveHour && fiveHour.percent >= 100 && fiveHour.resetsAt && fiveHour.resetsAt > now) return "atLimit";
  const left = (weekly.resetsAt - now) / HOUR_MS;
  const flatOut = weeklyHoursFlatOut(usage);
  if (flatOut && left < flatOut) return "wastage";
  if (weekly.percent > weekElapsed(weekly.resetsAt, now)!) return "over";
  return fiveHourHasRoom(usage, now) ? "under" : "held";
}

/** Each this many points under the week's pace lets one more Under run go at
 *  once, until an admin says otherwise (Global settings, server/underRuns.ts). */
export const UNDER_STEP_PERCENT = 5;

/**
 * How many things sent "Under" may be at work at once (server/underRuns.ts):
 * one, and one more for every `step` points the week's use is under the share
 * of the week gone — at 5, 8% under is two and 10% three. Spare pace is spent a
 * task at a time, but the further under, the more there is to spend. `step`
 * null: only ever one. One with no reading to say: whether anything goes at
 * all is `usageState`'s.
 */
export function underSlots(usage: ClaudeUsage | null, now: number, step: number | null = UNDER_STEP_PERCENT): number {
  const through = weekElapsed(usage?.weekly?.resetsAt, now);
  if (!usage?.weekly || through === null || !step || step <= 0) return 1;
  return 1 + Math.max(0, Math.floor((through - usage.weekly.percent) / step));
}

/** Whether `state` sends a queued row started "Under" or "Wastage": Under goes
 *  while under the week's pace and in wastage, Wastage only in wastage. */
export function usageSends(mode: "under" | "wastage", state: UsageState): boolean {
  return state === "wastage" || (mode === "under" && state === "under");
}

/** One usage reading, as `outsideRise` compares them. */
export interface UsageSample {
  fiveHour: UsageWindow | null;
  /** Tokens the box's Claudes spent in the interval that ENDED with this reading; null for unknown. */
  localTokens: number | null;
}

/**
 * The five-hour window's rise between two readings that nothing on this box
 * accounts for — the plan is being spent somewhere else — or 0.
 *
 * Only when the box spent NOTHING across both intervals: the one this rise
 * happened in and the one before it, because the endpoint counts a request a
 * little after it was made, and a turn that ended just before one reading can
 * show up in the next. Anything spent locally makes the rise unattributable —
 * tokens and percent do not convert at any documented rate — so this only
 * catches use elsewhere while the box is quiet. Never across a reset (the
 * window moved, or fell), and never on an unknown count.
 */
export function outsideRise(prev: UsageSample | null, cur: UsageSample): number {
  const a = prev?.fiveHour;
  const b = cur.fiveHour;
  if (!a || !b || b.percent <= a.percent) return 0;
  if (a.resetsAt !== null && b.resetsAt !== null && Math.abs(a.resetsAt - b.resetsAt) > 60_000) return 0;
  // `prev`'s count is the interval before the rise; `cur`'s, the rise's own.
  if (cur.localTokens !== 0 || prev?.localTokens !== 0) return 0;
  return b.percent - a.percent;
}

/**
 * What the five-hour window was spent by between two readings, in its own
 * points: the stats view's "used" chart. Within one window that is simply how
 * far it rose. Across a reset the meter FELL, and the fall is not use: what
 * counts then is what the new window already holds — it rarely reads exactly
 * zero by the first reading after — while whatever went in the old one's last
 * minutes was never reported. Null with nothing to compare against.
 *
 * A reset is the window's `resetsAt` moving when both readings carry one,
 * else a fall; a fall inside one window (the endpoint correcting itself) is 0.
 */
export function fiveHourRise(prev: UsageWindow | null | undefined, cur: UsageWindow | null): number | null {
  if (!prev || !cur) return null;
  const reset =
    prev.resetsAt !== null && cur.resetsAt !== null ? Math.abs(prev.resetsAt - cur.resetsAt) > 60_000 : cur.percent < prev.percent;
  return reset ? cur.percent : Math.max(0, cur.percent - prev.percent);
}

/**
 * The running "used elsewhere" tally with one more reading's rise added: kept
 * while the five-hour window is the same one, started over when it moves.
 */
export function addOutside(last: OutsideUsage | null | undefined, rise: number, fiveHour: UsageWindow | null, at: number): OutsideUsage | null {
  const resetsAt = fiveHour?.resetsAt ?? null;
  const same = !!last && (last.resetsAt === null || resetsAt === null || Math.abs(last.resetsAt - resetsAt) <= 60_000);
  const kept = same && (resetsAt === null || resetsAt > at) ? last : null;
  if (rise <= 0) return kept ?? null;
  return kept
    ? { points: kept.points + rise, firstAt: kept.firstAt, lastAt: at, resetsAt }
    : { points: rise, firstAt: at, lastAt: at, resetsAt };
}

/** The tally, if it is still about the five-hour window the reading is in. */
export function currentOutside(usage: ClaudeUsage | null, now: number): OutsideUsage | null {
  const o = usage?.outside;
  if (!o || o.points <= 0) return null;
  if (o.resetsAt !== null && o.resetsAt <= now) return null;
  return o;
}
